import { useCallback, useEffect, useRef } from "react";
import type { InputActionMeta } from "react-select";

import { useOrgUsersSelectLazyQuery } from "../graphql";

const MIN_SEARCH_LENGTH = 3;

type UseUserSearchSelectOptions = {
  excludeUserIds?: string[];
  signUpCompleted?: boolean;
};

type UserOption = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  fullName: string;
  email: string;
  profilePicUrl?: string | null;
};

export const useUserSearchSelect = ({
  excludeUserIds,
  signUpCompleted,
}: UseUserSearchSelectOptions = {}) => {
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [fetchUsers, { data, loading }] = useOrgUsersSelectLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  const search = useCallback(
    (searchTerm = "") => {
      fetchUsers({
        variables: {
          pagination: {
            page: 1,
            limit: 50,
            searchTerm: searchTerm || undefined,
          },
          signUpCompleted,
        },
      });
    },
    [fetchUsers, signUpCompleted]
  );

  useEffect(() => {
    search();
  }, [search]);

  const onInputChange = useCallback(
    (value: string, actionMeta: InputActionMeta) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (
        actionMeta.action === "set-value" ||
        actionMeta.action === "menu-close"
      ) {
        search();
        return;
      }
      if (actionMeta.action !== "input-change") return;
      if (value.length === 0) {
        search();
        return;
      }
      if (value.length < MIN_SEARCH_LENGTH) return;
      debounceRef.current = setTimeout(() => {
        search(value);
      }, 300);
    },
    [search]
  );

  const allUsers: UserOption[] =
    data?.currentUser?.organization?.usersPaginated?.results ?? [];

  const users = excludeUserIds?.length
    ? allUsers.filter((u) => !excludeUserIds.includes(u.id))
    : allUsers;

  return { users, loading, onInputChange };
};
